import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva } from "class-variance-authority";
import { ArrowLeft, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { OverlayHistoryEffect } from "@/components/ui/overlayHistory"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef(({ className, zIndex, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      // z-[300]: Sheet is a container peer of Dialog (also z-[300]) — equal z means
      // whichever opened later stacks on top (its portal mounts later in the DOM),
      // so a Sheet opened from a Dialog (e.g. ExercisePicker in the client routine
      // editor) and a Dialog opened from a Sheet both work.
      "fixed inset-0 z-[300] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    style={zIndex ? { zIndex } : undefined}
    {...props}
    ref={ref} />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-[300] gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-[100dvh] w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-[100dvh] w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

/* ── Mobile detection ─────────────────────────────────────── */
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  )
  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
    const handler = (e) => setIsMobile(e.matches)
    setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [breakpoint])
  return isMobile
}

/* ── Left-edge swipe-back gesture ──────────────────────────── */
function useSwipeBack(ref, onClose, enabled) {
  React.useEffect(() => {
    if (!enabled) return
    const el = ref.current
    if (!el) return
    let startX = 0, startY = 0
    const onStart = (e) => { startX = e.touches[0].clientX; startY = e.touches[0].clientY }
    const onEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX
      const dy = Math.abs(e.changedTouches[0].clientY - startY)
      if (startX < 40 && dx > 72 && dy < 80) onClose()
    }
    el.addEventListener('touchstart', onStart, { passive: true, capture: true })
    el.addEventListener('touchend', onEnd, { passive: true, capture: true })
    return () => {
      el.removeEventListener('touchstart', onStart, { capture: true })
      el.removeEventListener('touchend', onEnd, { capture: true })
    }
  }, [ref, onClose, enabled])
}

const SheetContent = React.forwardRef(({ side = "right", className, children, zIndex, overlayClassName, ...props }, ref) => {
  const { t } = useTranslation()
  const closeButtonRef = React.useRef(null)
  const contentRef = React.useRef(null)
  const isMobile = useIsMobile()

  const mergedRef = React.useCallback((node) => {
    contentRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }, [ref])

  const handleClose = React.useCallback(() => {
    closeButtonRef.current?.click()
  }, [])

  // Swipe-back on mobile
  useSwipeBack(contentRef, handleClose, isMobile)

  // Mobile: full-screen for bottom/right sheets (content sheets), keep left as nav sidebar
  if (isMobile && side !== 'left') {
    const mobileClassName = (className || '')
      .replace(/max-w-\S+/g, '')
      .replace(/max-h-\S+/g, '')
      .replace(/\bp-\d+\b/g, '')
      .replace(/\bsm:p-\d+\b/g, '')
      .replace(/\bgap-\d+\b/g, '')
      .replace(/\bborder-\S+/g, '')
      .replace(/\bborder\b/g, '')
      .replace(/\bshadow-\S+/g, '')
      .replace(/\brounded-\S+/g, '')
      .trim()

    return (
      <SheetPortal>
        <SheetPrimitive.Content
          ref={mergedRef}
          className={cn(
            "fixed inset-0 z-[300] flex flex-col bg-background text-foreground overflow-y-auto",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            mobileClassName
          )}
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', ...(zIndex ? { zIndex } : {}) }}
          {...props}>
          <OverlayHistoryEffect closeFn={handleClose} enabled={isMobile} />
          {children}
          <SheetPrimitive.Close ref={closeButtonRef} className="hidden" />
        </SheetPrimitive.Content>
      </SheetPortal>
    )
  }

  // Desktop (or left-side nav): original behavior
  return (
    <SheetPortal>
      <SheetOverlay zIndex={zIndex} className={overlayClassName} />
      <SheetPrimitive.Content ref={mergedRef} className={cn(sheetVariants({ side }), className)} style={zIndex ? { zIndex } : undefined} {...props}>
        <OverlayHistoryEffect closeFn={handleClose} enabled={isMobile} />
        <SheetPrimitive.Close
          ref={closeButtonRef}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <X className="h-4 w-4" />
          <span className="sr-only">{t('common.close')}</span>
        </SheetPrimitive.Close>
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

/* ── SheetHeader — on mobile: sticky back-arrow header ────────── */
const SheetHeader = ({
  className,
  children,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      // Mobile: sticky header bar with back arrow
      "max-sm:sticky max-sm:top-0 max-sm:z-10 max-sm:bg-background max-sm:border-b max-sm:border-border max-sm:flex-row max-sm:items-center max-sm:gap-2 max-sm:space-y-0 max-sm:px-2 max-sm:text-left",
      className
    )}
    style={{ minHeight: typeof window !== 'undefined' && window.innerWidth < 640 ? 56 : undefined }}
    {...props}>
    {/* Back arrow — mobile only */}
    <SheetPrimitive.Close
      className="hidden max-sm:flex items-center justify-center min-h-[44px] min-w-[44px] p-3 rounded-full hover:bg-muted active:bg-muted transition-colors flex-shrink-0"
      aria-label="Go back">
      <ArrowLeft className="h-5 w-5" />
    </SheetPrimitive.Close>
    <div className="max-sm:flex-1 max-sm:min-w-0">
      {children}
    </div>
  </div>
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      "max-sm:sticky max-sm:bottom-0 max-sm:bg-background max-sm:border-t max-sm:border-border max-sm:p-4",
      className
    )}
    {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold text-foreground",
      "max-sm:text-base max-sm:truncate",
      className
    )}
    style={{ fontFamily: typeof window !== 'undefined' && window.innerWidth < 640 ? "'Barlow Condensed', sans-serif" : undefined }}
    {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
